import { clip, type Clip } from '../clip.ts';
import { TAU, inOut, inQuad, outBack, outExpo, outQuad, phase, settle, span } from '../curves.ts';
import { apply, timeline, type KeyPose } from '../pose.ts';

/*
 * El Uro Viejo (models/uro.ts): `walk` (it grazes its way across the tail, slow, head low and swinging), `charge` (a
 * flat-out gallop, head down and horns first), `paw` (the wind-up before a charge: head down, a fore hoof scraping the
 * ground back, the body rocking), `idle` (blowing after a charge: flanks heaving, head hung, a toss now and then), `hit`,
 * `stun` (caught in a trap: braced, a fore leg held, the head jerking to tear free) and `die` (the fore knees buckle and
 * it goes down on its side; the view crumbles it to dust).
 *
 * Conventions as the mammoth's: limbs hang along −Y, a negative X swings them forward; +X on a lower leg folds the knee
 * back; +X on the body pitches it nose-down about the hind hips; +X on the head drops it; the L bones sit on −X.
 */

type B = 'root' | 'body' | 'head' | 'tail' | 'foreL' | 'foreL2' | 'foreR' | 'foreR2' | 'hindL' | 'hindL2' | 'hindR' | 'hindR2';
type P = KeyPose<B>;

/** A damped impulse at `t0` (0 before it). */
const kick = (t: number, t0: number, freq = 2.5, damp = 7): number => (t < t0 ? 0 : settle(t - t0, freq, damp));

/** Head carried low, tail hanging. */
const ST: P = { head: [0.12, 0, 0], tail: [0.08, 0, 0] };
const LAG: Partial<Record<B, number>> = { head: 0.04, tail: 0.08 };

/** Blowing after a charge: deep heaving breaths, the head hung and swinging, a toss of the horns now and then. */
const idle = (): Clip<B> =>
  clip<B>('idle', 2.4, true, (b, t) => {
    const p = phase(t, 2.4) * TAU;
    apply(b, ST);
    const br = Math.sin(p * 2);
    b.body.rot(0.02 + 0.015 * br, 0, 0.012 * Math.sin(p)).scale(1 + 0.018 * br);
    b.root.pos(0, -0.02 - 0.015 * br, 0);
    const toss = Math.max(0, Math.sin(p - 1.2)) ** 6;
    b.head.rot(0.22 - 0.35 * toss + 0.04 * br, 0.12 * Math.sin(p), 0.1 * toss);
    b.tail.rot(0.05, 0, 0.25 * Math.sin(p * 2 + 0.4));
    // A fore hoof shifts its weight.
    const lift = Math.max(0, Math.sin(p + 2)) ** 8;
    b.foreL.rot(-0.1 * lift);
    b.foreL2.rot(0.35 * lift);
  }, (t) => {
    const br = Math.sin(phase(t, 2.4) * TAU * 2);
    return [1 + 0.012 * br, 1 - 0.008 * br, 1 + 0.01 * br];
  });

/** A walk with the head low, grazing as it goes: four-beat, the head nodding and swinging. */
const walk = (dur = 1.7): Clip<B> =>
  clip<B>('walk', dur, true, (b, t) => {
    const th = phase(t, dur) * TAU;
    apply(b, ST, 0.8);
    const leg = (up: B, lo: B, off: number, A: number, fold: number): void => {
      const a = th - off * TAU;
      b[up].rot(-A * Math.sin(a) - 0.02);
      b[lo].rot(fold * Math.max(0, Math.cos(a)) ** 1.5);
    };
    leg('hindL', 'hindL2', 0, 0.3, 0.5);
    leg('foreL', 'foreL2', 0.25, 0.32, 0.65);
    leg('hindR', 'hindR2', 0.5, 0.3, 0.5);
    leg('foreR', 'foreR2', 0.75, 0.32, 0.65);
    const land = Math.max(0, -Math.cos(4 * th)) ** 3;
    b.root.pos(0.02 * Math.sin(th), -0.025 - 0.02 * land, 0).rot(0, 0, 0.02 * Math.sin(th - 0.4));
    b.body.rot(0.01 * Math.sin(2 * th + 0.5), 0.03 * Math.sin(th), 0.02 * Math.sin(th - 0.6));
    // The head goes down to crop the ash grass, then swings up and looks.
    const graze = Math.max(0, Math.sin(th * 0.5)) ** 2;
    b.head.rot(0.15 + 0.3 * graze + 0.04 * Math.sin(2 * th - 0.8), -0.12 * Math.sin(th - 0.3), -0.03 * Math.sin(th - 0.8));
    b.tail.rot(0.05 * Math.sin(2 * th - 1), 0, 0.25 * Math.sin(th - 1.5));
  }, (t) => {
    const k = Math.max(0, -Math.cos(4 * phase(t, dur) * TAU)) ** 3;
    return [1 + 0.01 * k, 1 - 0.012 * k, 1 + 0.01 * k];
  });

/**
 * The charge: a rotary gallop, the fore pair and the hind pair each landing close together, the body pitched forward
 * and the head down with the horns level, the tail streaming.
 */
const charge = (dur = 0.5): Clip<B> =>
  clip<B>('charge', dur, true, (b, t) => {
    const th = phase(t, dur) * TAU;
    apply(b, ST, 0.4);
    const leg = (up: B, lo: B, off: number, A: number, fold: number): void => {
      const a = th - off * TAU;
      b[up].rot(-A * Math.sin(a) - 0.05);
      b[lo].rot(fold * Math.max(0, Math.cos(a)) ** 1.2);
    };
    leg('hindL', 'hindL2', 0, 0.75, 0.9);
    leg('hindR', 'hindR2', 0.08, 0.75, 0.9);
    leg('foreL', 'foreL2', 0.45, 0.8, 1.2);
    leg('foreR', 'foreR2', 0.53, 0.8, 1.2);
    const push = Math.sin(th);
    b.root.pos(0, 0.06 * Math.max(0, push) - 0.02, 0.04 * push).rot(0, 0, 0.02 * Math.sin(th + 0.5));
    b.body.rot(0.1 + 0.07 * Math.sin(th + 1.2), 0, 0);
    b.head.rot(0.45 + 0.06 * Math.sin(th + 2), 0, 0);
    b.tail.rot(-0.9 + 0.15 * Math.sin(th * 2), 0, 0.12 * Math.sin(th));
  }, (t) => {
    const k = Math.max(0, Math.sin(phase(t, dur) * TAU + 2.4)) ** 3;
    return [1 + 0.02 * k, 1 - 0.025 * k, 1 + 0.02 * k];
  });

/** The wind-up: head down, the right fore hoof scraping back, again and again, the body rocking over it. */
const paw = (): Clip<B> =>
  clip<B>('paw', 0.9, true, (b, t) => {
    const ph = phase(t, 0.9);
    apply(b, ST);
    // Forward and up, then raked back along the ground.
    const fwd = ph < 0.35 ? outQuad(ph / 0.35) : 1 - inOut((ph - 0.35) / 0.65);
    b.foreR.rot(-0.55 * fwd + 0.25 * (1 - fwd) * span(ph, 0.35, 0.8, inQuad));
    b.foreR2.rot(0.9 * fwd);
    b.foreL.rot(0.06);
    b.body.rot(0.16 + 0.04 * Math.sin(ph * TAU), 0, 0.02 * Math.sin(ph * TAU));
    b.root.pos(0, -0.05, 0.03 * Math.sin(ph * TAU));
    b.head.rot(0.55 + 0.05 * Math.sin(ph * TAU * 2), 0.06 * Math.sin(ph * TAU), 0);
    b.hindL.rot(0.12);
    b.hindR.rot(0.12);
    b.tail.rot(-0.3, 0, 0.35 * Math.sin(ph * TAU * 2));
  });

/** It barely feels a blow: a jolt of the head, the body rocking. */
const hit = (): Clip<B> =>
  clip<B>('hit', 0.5, false, (b, t) => {
    apply(b, ST);
    const k = settle(t, 2.2, 8) * span(t, 0, 0.04, outQuad);
    b.body.rot(-0.04 * k, 0.03 * k, 0.03 * k);
    b.head.rot(-0.25 * k, 0.15 * k, 0.08 * k);
    b.root.pos(0, -0.01 * Math.abs(k), -0.05 * k);
  });

/** Caught in a trap: legs braced, the right fore held fast, the head jerking and twisting to tear free. */
const stun = (): Clip<B> =>
  clip<B>('stun', 1.2, true, (b, t) => {
    const ph = phase(t, 1.2);
    apply(b, ST);
    const j1 = kick(ph, 0.05, 6, 10);
    const j2 = kick(ph, 0.55, 6, 10);
    b.foreR.rot(-0.15, 0, 0.05);
    b.foreR2.rot(0.1);
    b.foreL.rot(0.12, 0, -0.08);
    b.hindL.rot(-0.18, 0, -0.06);
    b.hindR.rot(-0.18, 0, 0.06);
    b.body.rot(-0.06 + 0.04 * j1, 0.06 * j2, 0.05 * (j1 - j2));
    b.root.pos(0, -0.04, -0.08 * Math.abs(j1 + j2));
    b.head.rot(0.3 - 0.4 * j1 + 0.3 * j2, 0.35 * j2 - 0.25 * j1, 0.2 * j1);
    b.tail.rot(-0.5, 0, 0.5 * Math.sin(ph * TAU * 3));
  });

/** Felled: the head goes up with a last bellow, the fore knees buckle, the rear follows and it rolls onto its side. */
const die = (): Clip<B> =>
  clip<B>('die', 2.2, false, (b, t) => {
    const kneel: P = { foreL: [-0.3, 0, 0], foreL2: [1.7, 0, 0], foreR: [-0.25, 0, 0], foreR2: [1.65, 0, 0], root: [0, 0, 0, 0, -0.4, 0.1], body: [0.25, 0, 0], head: [0.45, 0.1, 0], tail: [0.2, 0, 0] };
    const flat: P = { ...kneel, root: [0, 0, 0.45, -0.05, -0.85, 0.05], body: [0.05, 0, 0], hindL: [-1.1, 0, -0.08], hindL2: [1.9, 0, 0], hindR: [-0.7, 0, 0.3], hindR2: [1.0, 0, 0], head: [0.5, 0.3, 0.3], tail: [0.4, 0, 0.3] };
    timeline(b, t, [
      [0, ST],
      [0.3, { ...ST, body: [-0.15, 0, 0], head: [-0.55, 0, 0], tail: [-0.5, 0, 0], foreL: [-0.2, 0, 0], foreR: [-0.15, 0, 0] }, outExpo],
      [0.8, kneel, inQuad],
      [0.95, { ...kneel, root: [0, 0, 0, 0, -0.37, 0.1] }, outQuad],
      [1.9, flat, inOut],
      [2.2, { ...flat, root: [0, 0, 0.43, -0.05, -0.85, 0.05] }, outBack],
    ], LAG);
  });

export const uroClips = (): Clip[] => [idle(), walk(), charge(), paw(), hit(), stun(), die()] as Clip[];

/** Clips by library kind (see ../rigs.ts). */
export const CLIPS: Record<string, () => Clip[]> = { uro: uroClips };
